import { useState } from 'react';
import { CIVS } from '../../gameplay/civilisations';
import { AGE_NAMES } from '../../gameplay/types';
import { MAP_TYPES, type MapSize, type MapType } from '../../world/maps/mapTypes';
import { DEFAULT_GAME_SPEED, GAME_SPEEDS, PLAYER_COLORS, defaultConfig, type GameConfig, type PlayerConfig, type StartingResources } from '../../engine/simulation/simulation';
import type { VictoryType } from '../../engine/simulation/systems/victory';
import type { AIDifficulty, AIPersonality } from '../../ai/aiController';
import { useApp } from '../store';
import { ColourPicker, freeColour } from './ColourPicker';

const DIFFS: AIDifficulty[] = ['beginner', 'standard', 'hard', 'expert'];
const PERS: AIPersonality[] = ['balanced', 'aggressive', 'defensive', 'economic', 'technological', 'expansionist'];
const VICTORIES: { key: VictoryType; label: string }[] = [
  { key: 'conquest', label: 'Conquest' },
  { key: 'wonder', label: 'Wonder' },
  { key: 'territorial', label: 'Territorial' },
  { key: 'economic', label: 'Economic' },
  { key: 'timeLimit', label: 'Time limit (score)' },
];

export function CivCard({ civKey }: { civKey: string }) {
  const c = CIVS.find((x) => x.key === civKey)!;
  return (
    <div className="civ-card">
      <h3>{c.name}</h3>
      <div className="tags">{c.specialisation.join(' · ')}</div>
      <div className="muted small" style={{ margin: '4px 0' }}>{c.setting}</div>
      <div><b>Bonuses:</b> {c.bonuses.join('; ')}</div>
      <div><b>Weaknesses:</b> {c.weaknesses.join('; ')}</div>
      <div><b>Unique:</b> {c.uniqueUnits.join(', ')}</div>
    </div>
  );
}

export function SkirmishSetup() {
  const { setScreen, prefs } = useApp();
  const [mapType, setMapType] = useState<MapType>('continental');
  const [mapSize, setMapSize] = useState<MapSize>('small');
  const [seed, setSeed] = useState(() => Math.floor(Math.random() * 1e6));
  const [startingAge, setStartingAge] = useState(0);
  const [gameSpeed, setGameSpeed] = useState(DEFAULT_GAME_SPEED);
  const [resources, setResources] = useState<StartingResources>('standard');
  const [popCap, setPopCap] = useState(200);
  const [victory, setVictory] = useState<VictoryType[]>(['conquest', 'wonder']);
  const [reveal, setReveal] = useState(false);
  const [spectate, setSpectate] = useState(false);
  const [players, setPlayers] = useState<PlayerConfig[]>([
    { name: prefs.playerName, civ: 'english', team: -1, color: PLAYER_COLORS[0] },
    { name: 'Computer 1', civ: 'french', team: -1, color: PLAYER_COLORS[1], ai: { difficulty: 'standard', personality: 'balanced' } },
  ]);
  const [focusCiv, setFocusCiv] = useState('english');

  const update = (i: number, p: Partial<PlayerConfig>) => setPlayers(players.map((x, j) => (j === i ? { ...x, ...p } : x)));
  const addAI = () => {
    if (players.length >= 4) return;
    const civ = CIVS[(players.length * 5) % CIVS.length].key;
    setPlayers([...players, { name: `Computer ${players.length}`, civ, team: -1, color: freeColour(players.map((p) => p.color)), ai: { difficulty: 'standard', personality: PERS[players.length % PERS.length] } }]);
  };
  const start = () => {
    const cfg: GameConfig = defaultConfig({
      seed, mapType, mapSize, startingAge, gameSpeed, startingResources: resources, popCapLimit: popCap, revealMap: reveal,
      players: players.map((p, i) => ({ ...p, color: p.color ?? PLAYER_COLORS[i], ai: p.ai ?? (spectate ? { difficulty: 'hard', personality: 'balanced' } : null) })),
    });
    cfg.victory.conditions = victory.length ? victory : ['conquest'];
    useApp.setState({ pendingConfig: cfg, screen: 'game', menuOpen: false, launch: spectate ? { spectate: true } : null });
  };

  return (
    <div className="menu-bg">
      <div className="menu-card">
        <div className="row spread"><h2>Skirmish</h2><button className="btn" onClick={() => setScreen('menu')}>Back</button></div>
        <div className="setup-grid">
          <div>
            <h3 style={{ fontSize: 15 }}>Players</h3>
            {players.map((p, i) => (
              <div className="player-card" key={i}>
                <div className="line">
                  <ColourPicker value={p.color ?? PLAYER_COLORS[i]} taken={players.map((q, j) => q.color ?? PLAYER_COLORS[j])} onChange={(c) => update(i, { color: c })} />
                  <input type="text" className="grow" value={p.name} onChange={(e) => update(i, { name: e.target.value })} aria-label="Player name" />
                  <select value={p.civ} onChange={(e) => { update(i, { civ: e.target.value }); setFocusCiv(e.target.value); }} aria-label="Civilisation" onFocus={() => setFocusCiv(p.civ)}>
                    {CIVS.map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
                  </select>
                  {i > 0 ? <button className="btn" onClick={() => setPlayers(players.filter((_, j) => j !== i))} aria-label="Remove player">×</button> : <span className="btn-spacer" />}
                </div>
                <div className="line sub">
                  {p.ai ? (
                    <>
                      <select value={p.ai.difficulty} onChange={(e) => update(i, { ai: { ...p.ai!, difficulty: e.target.value as AIDifficulty } })} aria-label="Difficulty">
                        {DIFFS.map((d) => <option key={d} value={d}>{d[0].toUpperCase() + d.slice(1)}</option>)}
                      </select>
                      <select className="grow" value={p.ai.personality} onChange={(e) => update(i, { ai: { ...p.ai!, personality: e.target.value as AIPersonality } })} aria-label="Personality">
                        {PERS.map((d) => <option key={d} value={d}>{d[0].toUpperCase() + d.slice(1)} AI</option>)}
                      </select>
                    </>
                  ) : <span className="muted small grow">Human player</span>}
                  <select value={p.team} onChange={(e) => update(i, { team: Number(e.target.value) })} aria-label="Team">
                    <option value={-1}>Free for all</option>
                    {[1, 2, 3, 4].map((t) => <option key={t} value={t}>Team {t}</option>)}
                  </select>
                </div>
              </div>
            ))}
            <div className="row" style={{ marginTop: 10 }}>
              <button className="btn" onClick={addAI} disabled={players.length >= 4}>+ Add AI opponent</button>
            </div>
            <CivCard civKey={focusCiv} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <h3 style={{ fontSize: 15 }}>Map & rules</h3>
            <label className="field">Map type
              <select value={mapType} onChange={(e) => setMapType(e.target.value as MapType)}>
                {MAP_TYPES.map((m) => <option key={m.key} value={m.key}>{m.name}</option>)}
              </select>
              <span className="small" style={{ textTransform: 'none' }}>{MAP_TYPES.find((m) => m.key === mapType)?.description}</span>
            </label>
            <label className="field">Map size
              <select value={mapSize} onChange={(e) => setMapSize(e.target.value as MapSize)}>
                <option value="small">Small (96×96)</option>
                <option value="medium">Medium (128×128)</option>
                <option value="large">Large (160×160)</option>
                <option value="huge">Huge (200×200)</option>
              </select>
            </label>
            <label className="field">Map seed
              <div className="row"><input type="number" value={seed} onChange={(e) => setSeed(Number(e.target.value) || 0)} style={{ flex: 1 }} /><button className="btn" onClick={() => setSeed(Math.floor(Math.random() * 1e6))}>Random</button></div>
            </label>
            <label className="field">Starting age
              <select value={startingAge} onChange={(e) => setStartingAge(Number(e.target.value))}>
                {AGE_NAMES.map((a, i) => <option key={a} value={i}>{a}</option>)}
              </select>
            </label>
            <label className="field">Game speed
              <select value={gameSpeed} onChange={(e) => setGameSpeed(Number(e.target.value))}>
                {GAME_SPEEDS.map((g) => <option key={g.value} value={g.value}>{g.name}</option>)}
              </select>
            </label>
            <label className="field">Starting resources
              <select value={resources} onChange={(e) => setResources(e.target.value as StartingResources)}>
                <option value="low">Low</option><option value="standard">Standard</option><option value="high">High</option>
              </select>
            </label>
            <label className="field">Population cap
              <select value={popCap} onChange={(e) => setPopCap(Number(e.target.value))}>
                {[75, 100, 150, 200].map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </label>
            <div className="field">Victory conditions
              <div className="row">
                {VICTORIES.map((v) => (
                  <label key={v.key} className="small" style={{ textTransform: 'none', color: 'var(--text)' }}>
                    <input type="checkbox" checked={victory.includes(v.key)} onChange={(e) => setVictory(e.target.checked ? [...victory, v.key] : victory.filter((x) => x !== v.key))} /> {v.label}
                  </label>
                ))}
              </div>
            </div>
            <label className="small"><input type="checkbox" checked={reveal} onChange={(e) => setReveal(e.target.checked)} /> Explored map</label>
            <label className="small"><input type="checkbox" checked={spectate} onChange={(e) => setSpectate(e.target.checked)} /> Spectate (your slot is played by a Hard AI)</label>
            <button className="btn primary" style={{ marginTop: 8, padding: 12, fontSize: 16 }} onClick={start}>Start Game</button>
          </div>
        </div>
      </div>
    </div>
  );
}
